import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, Copy } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { buildHotelTextQuote } from '@/utils/hotelTextQuote';
import { waLink } from '@/utils/phone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Hotel text quote" — Additive to WhatsApp Path B, same pattern as
 * FlightTextQuoteDialog. Generates a hotel-stay WhatsApp message from
 * itinerary.hotels[] (structured fields only) + the ONE lump-sum total from
 * the selected pricing option, shown in an editable box before sending.
 */
const HotelTextQuoteDialog = ({ open, onOpenChange, quote, hotels, totalCost }) => {
  const [text, setText] = useState('');
  const [hasHotels, setHasHotels] = useState(true);

  useEffect(() => {
    if (!open) return;
    const built = buildHotelTextQuote({ hotels, adults: quote?.adults, totalCost });
    setHasHotels(built.hasHotels);
    setText(built.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const openWhatsApp = async () => {
    window.open(waLink(quote.countryCode || '+91', quote.phone, text), '_blank', 'noopener,noreferrer');
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/mark-manual-sent`, {
        contextType: 'trip', contextId: quote.tripId, text,
      });
      toast.success('WhatsApp opened · logged in Chats as manually sent');
    } catch {
      toast.success('WhatsApp opened');
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="hotel-text-quote-dialog">
        <DialogHeader>
          <DialogTitle>Hotel text quote</DialogTitle>
          <DialogDescription>
            Hotel details come from the itinerary automatically. Total is the selected
            pricing option's lump-sum total — no per-night rate is shown.
          </DialogDescription>
        </DialogHeader>

        {!hasHotels ? (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="hotel-text-no-hotels">
            No hotels found on this itinerary. Add at least one hotel (with a name) first,
            then reopen this.
          </div>
        ) : (
          <div>
            <Textarea
              data-testid="hotel-text-quote-textarea"
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="font-mono text-xs"
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="hotel-text-cancel-btn">Cancel</Button>
          {hasHotels && (
            <>
              <Button variant="outline" onClick={copyText} data-testid="hotel-text-copy-btn">
                <Copy className="h-4 w-4 mr-2" />Copy
              </Button>
              <Button onClick={openWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="hotel-text-open-whatsapp-btn">
                <MessageCircle className="h-4 w-4 mr-2" />Open WhatsApp
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HotelTextQuoteDialog;
